import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Users, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UpcomingTrainingsSection = ({ pageSlug, title = "Upcoming Training Sessions" }) => {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrainings();
  }, [pageSlug]);

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/trainings-for-page/${pageSlug}`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Failed to fetch trainings:', error);
      setTrainings([]);
    } finally {
      setLoading(false);
    }
  };

  const getModeColor = (mode) => {
    const colors = {
      'Classroom': 'bg-blue-100 text-blue-800',
      'Online': 'bg-green-100 text-green-800',
      'Hybrid': 'bg-purple-100 text-purple-800',
      'On-Site': 'bg-orange-100 text-orange-800'
    };
    return colors[mode] || 'bg-gray-100 text-gray-800';
  };

  const handleRegister = (training) => {
    if (training.slug) {
      navigate(`/training-event/${training.slug}`);
    } else {
      navigate('/training-calendar');
    }
  };

  if (loading) {
    return (
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading upcoming training sessions...</p>
          </div>
        </div>
      </section>
    );
  }

  // Don't show section if no trainings
  if (trainings.length === 0) {
    return null;
  }

  return (
    <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full text-sm mb-4 font-semibold">
              REGISTER NOW
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our upcoming training sessions and earn your professional certification
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainings.map((training, index) => (
              <motion.div
                key={training.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="hover:shadow-xl transition-all duration-300 h-full border-2 hover:border-blue-200">
                  <CardContent className="p-6 flex flex-col h-full">
                    {/* Training Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-4 line-clamp-2">
                      {training.title}
                    </h3>

                    {/* Training Details */}
                    <div className="space-y-3 mb-6 flex-grow">
                      {/* Date */}
                      <div className="flex items-center gap-3 text-gray-700">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Calendar className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Training Date</p>
                          <p className="text-sm">
                            {format(new Date(training.date), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>

                      {/* Duration */}
                      {training.duration && (
                        <div className="flex items-center gap-3 text-gray-700">
                          <div className="bg-purple-100 p-2 rounded-lg">
                            <Clock className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Duration</p>
                            <p className="text-sm">{training.duration}</p>
                          </div>
                        </div>
                      )}

                      {/* Mode */}
                      {training.mode && (
                        <div className="flex items-center gap-3 text-gray-700">
                          <div className="bg-green-100 p-2 rounded-lg">
                            <TrendingUp className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Training Mode</p>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getModeColor(training.mode)}`}>
                              {training.mode}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Location */}
                      {training.location && (
                        <div className="flex items-center gap-3 text-gray-700">
                          <div className="bg-orange-100 p-2 rounded-lg">
                            <MapPin className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Location</p>
                            <p className="text-sm">{training.location}</p>
                          </div>
                        </div>
                      )}

                      {/* Seats Available */}
                      {training.seats_available && (
                        <div className="flex items-center gap-3 text-gray-700">
                          <div className="bg-indigo-100 p-2 rounded-lg">
                            <Users className="h-5 w-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Seats Available</p>
                            <p className="text-sm font-semibold text-indigo-600">
                              {training.seats_available} seats
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Price */}
                      {training.price && (
                        <div className="pt-2">
                          <p className="text-sm text-gray-600">Training Fee</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {training.is_paid ? training.price : 'Complimentary'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Register Button */}
                    <Button
                      onClick={() => handleRegister(training)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
                    >
                      Register Now
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/training-calendar')}
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-6 text-lg"
              size="lg"
            >
              View Complete Training Calendar
              <Calendar className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
